// The line Show solution plays: not only the puzzle's first move but the
// engine's line from the position, as far as the puzzle's reason is realised.
//
// The stored puzzle keeps a short line: one move for a blunder replayed
// ("find the better move") and for a winning advantage, the settled material
// for a captured prize. The scan keeps more: the engine's principal variation
// from the position (`lines[0].pv`), and that is what the solution walks when
// it begins with the puzzle's move; the puzzle's own line otherwise.
//
// Where it stops. The scan carries one score per line, at its end, and none
// per ply, so the measure along the line is material: the reader's lead in
// pawns (Q9 R5 B3 N3 P1) over the lead they had at the puzzle's position.
// - A line that mates plays to the mate (`mate`).
// - Otherwise the line plays to the first ply after which the reader's gain
//   reaches the rule that made the puzzle (`missGapPawns` for a missed
//   opportunity, `blunderPawns` for a blunder replayed) and holds it for the
//   rest of the line (`gain`): an exchange in progress is played through, so
//   the solution never stops on a capture about to be recaptured.
// - A line that never realises its gain plays to its end (`end`): the pv's
//   length, a mate prize's whole line when the pv falls short of the mate.
// At least the first move, when it is legal from the position.
import type { Chess } from '@chess/api-chess';
import { uciToInput } from '@chess/api-chess';
import type { MistakeRules, San, Uci } from '@chess/api-model';
import { balance, type Puzzle } from './finder.ts';

export type SolutionReason = 'mate' | 'gain' | 'end';

export interface SolutionLine {
  /** The moves to play, both sides, the reader's first. */
  moves: Uci[];
  /** The same moves in SAN, for the status line. */
  sans: San[];
  reason: SolutionReason;
}

/** The rules of chess the line needs: to play a move and read the position after it. */
export type SolutionRules = Pick<Chess, 'play'>;

/** The rules that made the puzzle: the gap behind a miss and the loss behind a blunder, in pawns. */
export type SolutionGaps = Pick<MistakeRules, 'blunderPawns' | 'missGapPawns'>;

/** The line to walk: the scan's pv when it begins as the puzzle does and is no shorter, else the puzzle's own line. */
export function lineToShow(puzzle: Pick<Puzzle, 'line'>, pv: Uci[] | undefined): Uci[] {
  if (pv && pv.length >= puzzle.line.length && pv[0] === puzzle.line[0]) return pv;
  return puzzle.line;
}

/**
 * The moves Show solution plays for an engine puzzle and why they stop
 * there. `pv` is the scan's best line from the puzzle's position, when the
 * scan has it; `gaps` the mistake rules the puzzle was found under.
 */
export function solutionLineOf(puzzle: Pick<Puzzle, 'kind' | 'side' | 'fen' | 'line' | 'prize'>, pv: Uci[] | undefined, gaps: SolutionGaps, chess: SolutionRules): SolutionLine {
  const source = lineToShow(puzzle, pv);
  const moves: Uci[] = [];
  const sans: San[] = [];
  const gains: number[] = [];
  const opening = balance(puzzle.fen, puzzle.side);
  let fen = puzzle.fen;
  let mated = false;
  for (const uci of source) {
    const played = chess.play(fen, uciToInput(uci));
    if (!played) break;
    moves.push(uci);
    sans.push(played.move.san);
    fen = played.fen;
    gains.push(balance(fen, puzzle.side) - opening);
    if (played.move.flags.checkmate) {
      mated = true;
      break;
    }
  }
  if (mated) return { moves, sans, reason: 'mate' };
  if (puzzle.prize.kind !== 'mate') {
    const target = puzzle.kind === 'tactic' ? gaps.missGapPawns : gaps.blunderPawns;
    for (let i = 0; i < gains.length; i++) {
      if (gains.slice(i).every((g) => g >= target)) return { moves: moves.slice(0, i + 1), sans: sans.slice(0, i + 1), reason: 'gain' };
    }
  }
  return { moves, sans, reason: 'end' };
}
